import type { FC } from "react";
import { useEffect, useState } from "react";

import type { UserTutoringMinimumFragment } from "api/generated";
import { useGetTutoringThemesMinimumQuery, useGetUserThemesQuery, useGetWeeklyChallengesQuery } from "api/generated";
import { Loading } from "components";
import { useAppSelector } from "store/hooks";
import { getUserEmail } from "store/slices/user";
import type { ThemeRowDataType } from "utils/common/themes";
import { parseThemes } from "utils/common/themes";
import { processApiError } from "utils/processApiError";
import ThemeRow from "./ThemeRow";
import ThemeRowHead from "./ThemeRowHead";

interface Props {
  subjectId: string | null;
}

const TutoringThemes: FC<Props> = ({ subjectId }) => {
  const { data: userThemes, isLoading: isUserThemesLoading, error: userThemesApiError } = useGetUserThemesQuery();
  const {
    data: tutoringThemes,
    isLoading: isTutoringThemesLoading,
    error: tutoringThemesApiError,
  } = useGetTutoringThemesMinimumQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: weeklyChallenges, error: weeklyChallengesApiError } = useGetWeeklyChallengesQuery();

  const userEmail = useAppSelector(getUserEmail);
  const [themes, setThemes] = useState<ThemeRowDataType[]>([]);
  const [activeSort, setActiveSort] = useState<{ type: string; direction: "asc" | "desc" }>({
    type: "points",
    direction: "desc",
  });

  useEffect(() => {
    if (userThemesApiError === undefined) return;

    processApiError(userThemesApiError);
  }, [userThemesApiError]);

  useEffect(() => {
    if (tutoringThemesApiError === undefined) return;

    processApiError(tutoringThemesApiError);
  }, [tutoringThemesApiError]);

  useEffect(() => {
    if (weeklyChallengesApiError === undefined) return;

    processApiError(weeklyChallengesApiError);
  }, [weeklyChallengesApiError]);

  if (subjectId === null) return null;

  useEffect(() => {
    if (userThemes === undefined || tutoringThemes === undefined || !userEmail) return;

    const subjectThemes = userThemes.userThemes.filter(theme => theme.theme?.subject === subjectId);
    const subjectTutoringList: UserTutoringMinimumFragment[] = [];
    subjectThemes.forEach(userTheme => {
      const tutoring = tutoringThemes.userTutoringAll.find(tutoring => tutoring.theme === userTheme.theme?.id);
      if (!tutoring) return;

      subjectTutoringList.push(tutoring);
    });

    setThemes(parseThemes(userThemes, tutoringThemes.userTutoringAll, subjectId, userEmail));
  }, [userThemes, tutoringThemes, userEmail]);

  if (isUserThemesLoading || isTutoringThemesLoading) {
    return <Loading />;
  }

  const sort = (type: string, by: "asc" | "desc") => {
    if (type !== "points" && type !== "envelopes") {
      console.error(`Unsupported sort type: ${type}`);
      return;
    }

    const key = type === "points" ? "avgPoints" : "assignmentsRed";
    const sortedThemes = [...themes].sort((themeA, themeB) => {
      const a = themeA[key] ?? 0;
      const b = themeB[key] ?? 0;

      return by === "asc" ? a - b : b - a;
    });

    setActiveSort({ type, direction: by });
    setThemes(sortedThemes);
  };

  const getChallenge = (tutoringId?: string) => {
    if (!tutoringId || !weeklyChallenges) return;

    return weeklyChallenges?.weeklyChallenges.find(challenge => challenge.theme === tutoringId);
  };

  // TODO: Handle empty themes
  if (!themes.length) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "30vh" }}>
        <h3 style={{ color: "#eb5757", textAlign: "center" }}>
          Jejda. Tvoje doučování se nám rozbilo a ještě jsme ho nestihli opravit. Zkus to později.
        </h3>
      </div>
    );
  }

  return (
    <div className="themes-list">
      <ThemeRowHead sort={sort} activeSort={activeSort} />
      {themes.map(theme => (
        <ThemeRow key={theme.tutoringId} theme={theme} challenge={getChallenge(theme.themeId)} />
      ))}
    </div>
  );
};

export default TutoringThemes;
